import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, HardHat, Users, FileText, UsersRound, TrendingUp, LogOut, Menu, X,
  FileSignature, Bell, ClipboardList, CalendarDays, Wrench, UserPlus, Search, Settings as SettingsIcon,
} from "lucide-react";
import { useState } from "react";
import SearchModal from "@/components/SearchModal";

// Дашборд stands alone above two grouped sections (see task: "Рабочий процесс"
// vs "Клиенты и бизнес"). Договоры/Angebote are two existing, separate routes —
// kept intact — grouped only visually under one sub-heading.
const WORKFLOW_LINKS = [
  { to: "/objects",   label: "Объекты",       icon: HardHat,        adminOnly: false, workerAccess: true },
  { to: "/planning",  label: "Планирование",  icon: ClipboardList,  adminOnly: true },
  { to: "/workers",   label: "Сотрудники",    icon: UsersRound,     adminOnly: true },
  { to: "/calendar",  label: "Календарь",     icon: CalendarDays,   adminOnly: true },
  { to: "/tools",     label: "Инструменты",   icon: Wrench,         adminOnly: true },
];

const BUSINESS_LINKS = [
  { to: "/clients",   label: "Клиенты",       icon: Users,          adminOnly: true },
  { to: "/leads",     label: "Лиды",          icon: UserPlus,       adminOnly: true },
];

const CONTRACTS_GROUP = [
  { to: "/contracts", label: "Договоры",       icon: FileSignature, adminOnly: true },
  { to: "/quotes",    label: "Предложения",    icon: FileText,      adminOnly: true },
];

const BUSINESS_LINKS_TAIL = [
  { to: "/finances",  label: "Финансы",       icon: TrendingUp,     adminOnly: true },
  { to: "/reminders", label: "Напоминания",   icon: Bell,           adminOnly: true },
];

function visibleFor(links, isAdmin) {
  return links.filter((l) => (isAdmin ? true : l.workerAccess));
}

function NavItem({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${to.slice(1)}`}
      onClick={onClick}
      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
    >
      <Icon size={17} />
      <span>{label}</span>
    </NavLink>
  );
}

function GroupLabel({ children }) {
  return (
    <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-600 font-semibold">
      {children}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const closeMobile = () => setMobileOpen(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100 grain-bg">
      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} isAdmin={isAdmin} />

      {/* Top bar (mobile) */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-[#0b0f17]/90 backdrop-blur border-b border-[#212b3d]">
        <div className="flex items-center gap-2">
          <span className="font-display font-extrabold text-amber-500 tracking-tight text-lg">Grandmont Group</span>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="mobile-search-button" onClick={() => setSearchOpen(true)} className="p-2 rounded-md hover:bg-[#1c2434]">
            <Search size={18} />
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((v) => !v)} className="p-2 rounded-md hover:bg-[#1c2434]">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${mobileOpen ? "block" : "hidden"} lg:block fixed lg:sticky top-0 lg:top-0 left-0 z-40 lg:z-10 w-64 h-screen bg-[#0f1521] lg:bg-transparent border-r border-[#212b3d] flex-shrink-0 overflow-y-auto`}
        >
          <div className="hidden lg:flex flex-col px-5 pt-6 pb-4 border-b border-[#212b3d]">
            <span className="font-display font-extrabold text-2xl text-amber-500 tracking-tight leading-none">Grandmont</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Chemnitz</span>
          </div>

          <nav className="p-3 pb-2 mt-2 lg:mt-4">
            <NavItem to="/dashboard" label="Дашборд" icon={LayoutDashboard} onClick={closeMobile} />

            <GroupLabel>Рабочий процесс</GroupLabel>
            <div className="space-y-1">
              {visibleFor(WORKFLOW_LINKS, isAdmin).map((l) => <NavItem key={l.to} {...l} onClick={closeMobile} />)}
            </div>

            {isAdmin && (
              <>
                <GroupLabel>Клиенты и бизнес</GroupLabel>
                <div className="space-y-1">
                  {BUSINESS_LINKS.map((l) => <NavItem key={l.to} {...l} onClick={closeMobile} />)}
                  <div className="pl-3 mt-1 space-y-1 border-l border-[#212b3d] ml-4">
                    <div className="text-[10px] text-slate-600 px-3 pt-1 pb-0.5">Договоры &amp; Angebote</div>
                    {CONTRACTS_GROUP.map((l) => <NavItem key={l.to} {...l} onClick={closeMobile} />)}
                  </div>
                  {BUSINESS_LINKS_TAIL.map((l) => <NavItem key={l.to} {...l} onClick={closeMobile} />)}
                </div>
              </>
            )}

            <div className="mt-4 pt-3 border-t border-[#212b3d] space-y-1">
              <button
                data-testid="nav-search"
                onClick={() => { setSearchOpen(true); closeMobile(); }}
                className="nav-link w-full text-left"
              >
                <Search size={17} />
                <span>Поиск</span>
              </button>
              <NavItem to="/settings" label="Настройки" icon={SettingsIcon} onClick={closeMobile} />
            </div>
          </nav>

          <div className="p-3 border-t border-[#212b3d] bg-[#0f1521] lg:sticky lg:bottom-0">
            <div className="flex items-center gap-3 px-2 py-2 mb-2">
              <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 font-semibold">
                {user?.name?.[0] ?? "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {isAdmin ? "Владелец / Админ" : "Мастер / Рабочий"}
                </div>
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={doLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition"
            >
              <LogOut size={16} /> Выйти
            </button>
          </div>
        </aside>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/50" onClick={closeMobile} />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 relative z-10">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
